import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { getJSONFromString } from '../../../utils/utils';
import { errorNotification } from '../../../modules/Notifications/notificationDecorator';
import YMapPolygon from './YMapPolygon';
import {
  BASE_MAP_CONTROLS,
  MAP_SEARCH_UNAVAILABLE_MESSAGE,
  probeMapSearch,
} from './mapAvailability';
import './YMap.scss';

const MAP_ERROR_MESSAGE = 'Карта недоступна. Поиск и пробки временно отключены.';
export const TRAFFIC_STATISTICS_UNAVAILABLE_MESSAGE = 'Статистика пробок недоступна на текущем контуре';
const MAP_TOKEN = (process.env.REACT_APP_MAP_TOKEN || '').trim();
const MAP_SUGGEST_TOKEN = (process.env.REACT_APP_MAP_SUGGEST_TOKEN || '').trim();
const MAP_SEARCH_CONFIGURED = Boolean(MAP_TOKEN && MAP_SUGGEST_TOKEN);
let mapSearchAvailabilityPromise = null;

export default function YMap(props) {
  const {
    className,
    polygons = [],
    setMapInstance,
  } = props;
  const blockRef = useRef(null);
  const [instance, setInstance] = useState(false);
  const [yMapInit, setYMapInit] = useState(false);
  const [mapError, setMapError] = useState('');
  const [trafficEnabled, setTrafficEnabled] = useState(false);
  const [trafficMenuOpen, setTrafficMenuOpen] = useState(false);
  const [trafficMessage, setTrafficMessage] = useState('');
  const [searchMessage, setSearchMessage] = useState(MAP_SEARCH_CONFIGURED ? '' : MAP_SEARCH_UNAVAILABLE_MESSAGE);
  const trafficProviderRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const MAP_CENTER = getJSONFromString(process.env.REACT_APP_MAP_CENTER) || [];
  const createMap = controls => new window.ymaps.Map(
    blockRef.current,
    {
      center: MAP_CENTER,
      zoom: 12,
      controls,
    },
    {
      yandexMapType: 'future-map',
    },
  );
  const init = () => {
    setYMapInit(true);
    if (instance) {
      instance.destroy();
    }
    if (window.ymaps && blockRef.current) {
      let i;
      try {
        i = createMap(BASE_MAP_CONTROLS);
      } catch (e) {
        try {
          i = createMap([]);
        } catch (fallbackError) {
          setMapError(MAP_ERROR_MESSAGE);
          errorNotification('Загрузка карты', MAP_ERROR_MESSAGE);
          return;
        }
      }
      mapInstanceRef.current = i;
      setInstance(i);
      if (setMapInstance && typeof setMapInstance === 'function') {
        setMapInstance(i);
      }
      if (MAP_SEARCH_CONFIGURED) {
        if (!mapSearchAvailabilityPromise) {
          mapSearchAvailabilityPromise = probeMapSearch(MAP_TOKEN, MAP_SUGGEST_TOKEN, window.ymaps);
        }
        mapSearchAvailabilityPromise.then(available => {
          if (mapInstanceRef.current !== i) {
            return;
          }
          if (!available) {
            setSearchMessage(MAP_SEARCH_UNAVAILABLE_MESSAGE);
            return;
          }
          try {
            i.controls.add('searchControl');
            setSearchMessage('');
          } catch (e) {
            setSearchMessage(MAP_SEARCH_UNAVAILABLE_MESSAGE);
          }
        });
      }
    } else {
      setMapError(MAP_ERROR_MESSAGE);
      errorNotification('Загрузка карты', MAP_ERROR_MESSAGE);
    }
  };
  useEffect(() => {
    if (window.ymaps) {
      window.ymaps.ready(init);
    } else {
      setMapError(MAP_ERROR_MESSAGE);
      errorNotification('Загрузка карты', MAP_ERROR_MESSAGE);
    }
    return () => {
      if (trafficProviderRef.current) {
        trafficProviderRef.current.setMap(null);
        trafficProviderRef.current = null;
      }
      mapInstanceRef.current = null;
    };
  }, []);

  useEffect(() => () => {
    if (instance) {
      instance.destroy();
    }
  }, [instance]);

  const getTrafficProvider = () => {
    if (!trafficProviderRef.current && window.ymaps?.traffic?.provider?.Actual) {
      trafficProviderRef.current = new window.ymaps.traffic.provider.Actual({}, { infoLayerShown: true });
    }
    return trafficProviderRef.current;
  };

  const toggleTraffic = () => {
    if (!instance || !window.ymaps) {
      setTrafficMessage(MAP_ERROR_MESSAGE);
      return;
    }

    try {
      const provider = getTrafficProvider();
      if (!provider) {
        setTrafficMessage('Пробки недоступны на текущем контуре');
        return;
      }
      provider.setMap(trafficEnabled ? null : instance);
      setTrafficEnabled(!trafficEnabled);
      setTrafficMessage('');
    } catch (e) {
      setTrafficMessage('Пробки недоступны на текущем контуре');
    }
  };

  const showTrafficStatisticsUnavailable = () => {
    setTrafficMessage(TRAFFIC_STATISTICS_UNAVAILABLE_MESSAGE);
    errorNotification('Пробки', TRAFFIC_STATISTICS_UNAVAILABLE_MESSAGE);
    setTrafficMenuOpen(false);
  };

  return (
    <>
      <div className="ymap-wrapper">
        <div id="map" className={classNames('ymap', className)} ref={blockRef} />
        {yMapInit && !mapError ? (
          <div className="ymap-traffic-control">
            <button
              type="button"
              className={classNames('ymap-traffic-button', { active: trafficEnabled })}
              onClick={toggleTraffic}
            >
              Пробки
            </button>
            <button
              type="button"
              className="ymap-traffic-settings"
              title="Настройки пробок"
              onClick={() => setTrafficMenuOpen(!trafficMenuOpen)}
            >
              ⚙
            </button>
            {trafficMenuOpen ? (
              <div className="ymap-traffic-menu">
                <button type="button" onClick={toggleTraffic}>
                  Сейчас
                </button>
                <button type="button" onClick={showTrafficStatisticsUnavailable}>
                  Статистика
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
        {mapError || searchMessage || trafficMessage ? (
          <div className="ymap-status-list">
            {mapError ? <div className="ymap-status">{mapError}</div> : null}
            {searchMessage ? <div className="ymap-status">{searchMessage}</div> : null}
            {trafficMessage ? <div className="ymap-status">{trafficMessage}</div> : null}
          </div>
        ) : null}
      </div>
      {yMapInit ? (
        polygons.filter(Boolean).map((polygon, index) => <YMapPolygon key={polygon?.properties?.get?.('id') || index} instance={instance} polygon={polygon} />)
      ) : null}
    </>
  );
}
